import { Heading, Stack, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { ISubscriptionChange, ISubscriptionChangeAddonChange } from 'api/types';
import { DataTable } from 'components/Table/DataTable';
import { dateTimeTransformer } from 'helpers/dateTransformer';
import useSubscriptionChangesQuery from 'hooks/useSubscriptionChangesQuery';
import useSubscriptionPlanQuery, { ISubscriptionPlan } from 'hooks/useSubscriptionPlanQuery';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SubscriptionChangesCard = (props: { workshopId: number }) => {
  const { t } = useTranslation();
  const { workshopId } = props;
  const subscriptionChangesQuery = useSubscriptionChangesQuery(workshopId);

  const allSubscriptionChangesObjects: ISubscriptionChange[] = subscriptionChangesQuery?.data || [];
  const subscriptionPlanQuery = useSubscriptionPlanQuery();
  const allSubscriptionPlansObjects: ISubscriptionPlan[] = subscriptionPlanQuery?.data || [];
  const subscriptionData = allSubscriptionChangesObjects;
  const subscriptionPlans = allSubscriptionPlansObjects;

  const subscriptionChangesData = subscriptionData?.reduce(
    (acc, curr) => {
      const subscriptionPlanId = subscriptionPlans.find((subscriptionPlan) => subscriptionPlan.id === curr.plan_id);
      const subscriptionPlanName = subscriptionPlanId?.name;
      acc.push({
        name: subscriptionPlanName,
        id: curr.plan_id,
        changed_at: curr.changed_at,
        billing_cycle: curr.billing_cycle,
        addon_changes: curr.addon_changes,
      });
      return acc;
    },
    [] as { name?: string; id: string; changed_at: string; billing_cycle: string; addon_changes: ISubscriptionChangeAddonChange[] }[],
  );
  const columns = useMemo(
    () => [
      {
        Header: t('chargebee:subscription.subscription_plan.label'),
        Accessor: 'name',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const planName = row?.original?.name;
          return <Text>{planName ? `${planName}` : '-'}</Text>;
        },
      },
      {
        Header: t('chargebee:subscription.subscription_changes.activated_on'),
        Accessor: 'changed_at',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const activationDay = row?.original?.changed_at;
          return <Text>{activationDay ? dateTimeTransformer(activationDay) : '-'}</Text>;
        },
      },
      {
        Header: t('chargebee:subscription.billing_period.label'),
        Accessor: 'billing_cycle',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const billingCycle = row?.original?.billing_cycle;
          return <Text>{t(`chargebee:paymentIntervals.${billingCycle}`)}</Text>;
        },
      },
      {
        Header: t('chargebee:subscription.subscription_changes.addons'),
        Accessor: 'addon_changes',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const addonChanges: ISubscriptionChangeAddonChange[] = row?.original?.addon_changes || [];

          if (!addonChanges.length) {
            return <Text>-</Text>;
          }

          return (
            <Wrap>
              {addonChanges.map((addonChange, index) => (
                <WrapItem key={`${addonChange.cb_addon_id}-${index}`}>
                  <Tag colorScheme={addonChange.action === 'deactivated' ? 'gray' : addonChange.is_trial ? 'blue' : 'green'}>
                    {t(`chargebee:addons.${addonChange.cb_addon_id}.name`)}
                    {' · '}
                    {addonChange.is_trial && addonChange.action !== 'deactivated'
                      ? t('chargebee:addons.data_table.trial')
                      : t(`chargebee:addons.data_table.${addonChange.action}`)}
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Stack direction={['column', 'row']} w={'100%'}>
      <Stack direction="column" w="full">
        <Heading as="h4" size="xxs" pt={10}>
          {t('chargebee:subscription.subscription_changes.label')}
        </Heading>
        <DataTable
          hasFilters={false}
          hasSearchInput={false}
          data-test-id="subscription-changes-table"
          data={subscriptionChangesData || []}
          columns={columns}
          loading={subscriptionChangesQuery.isLoading}
          isMoreThenOnePage={false}
        />
      </Stack>
    </Stack>
  );
};
export default SubscriptionChangesCard;
